import { useSyncExternalStore } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  duration?: number;
}

let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const MAX_VISIBLE = 3;
/** How long a toast stays up, drained by the bar the host draws. */
const DEFAULT_DURATION = 10_000;

/** How long the toast takes to fade once its time is nearly up. */
export const TOAST_FADE_MS = 600;

function emit() {
  for (const listener of [...listeners]) listener();
}

/** Removes one toast and stops its timer. */
export function dismissToast(id: string) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toasts = toasts.filter((entry) => entry.id !== id);
  emit();
}

/** Removes every toast and stops every timer. */
export function dismissAllToasts() {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  toasts = [];
  emit();
}

let sequence = 0;

/** Shows one toast and returns its id. */
export function toast(input: Omit<Toast, 'id'>) {
  const id = `toast-${++sequence}`;
  const duration = input.duration ?? DEFAULT_DURATION;

  toasts = [...toasts, { ...input, id, duration }].slice(-MAX_VISIBLE);
  emit();

  if (duration > 0) timers.set(id, setTimeout(() => dismissToast(id), duration));
  return id;
}

/** Returns the toasts on screen now. */
export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => toasts,
    () => toasts,
  );
}

/** Returns the functions that show and remove a toast. */
export function useToast() {
  return { toast, dismiss: dismissToast };
}
